import * as Sentry from '@sentry/react';
import { LifecyclePlaybook } from 'components/dashboard';
import WelcomePage from 'components/dashboard/WelcomePage';
import { ActiveCampaignConnect } from 'components/integrations/activeCampaign/ActiveCampaignConnect';
import { DripConnect } from 'components/integrations/drip/DripConnect';
import { FacebookConnect } from 'components/integrations/facebook/FacebookConnect';
import { GoogleConnect } from 'components/integrations/google/GoogleConnect';
import { KlaviyoConnect } from 'components/integrations/klaviyo/KlaviyoConnect';
import { KlaviyoListImport } from 'components/integrations/klaviyo/KlaviyoListImport';
import { MailChimpConnect } from 'components/integrations/mailChimp/MailChimpConnect';
import { OmnisendConnect } from 'components/integrations/omnisend/OmnisendConnect';
import { PostscriptConnect } from 'components/integrations/postscript/PostscriptConnect';
import { YotpoConnect } from 'components/integrations/yotpo/YotpoConnect';
import { SegmentIntegrations } from 'components/segments';
import { LoadingSpinner } from 'components/ui';
import { Location } from 'history';
import { DashboardPage, NotFoundPage } from 'pages';
import {
  AddStore,
  ChangeEmailConfirmation,
  ChangePassword,
  ChangePlanPage,
  ConfirmPage,
  Invitation,
  LoginPage,
  ManageStores,
  NewPassword,
  OnboardingPage,
  Referrals,
  RegisterPage,
  ResetPassword,
  Settings,
} from 'pages/account';
import { ChargeConfirmationPage } from 'pages/account/ChargeConfirmationPage';
import {
  CustomerCohort,
  ExecutiveSummary,
  PurchaseTiming,
} from 'pages/analytics';
import {
  IntegrationsPage,
  ShopifyAuthentication,
  ShopifyWelcome,
} from 'pages/integrations';
import ProductCohort from 'pages/products/ProductCohort';
import ProductJourneyPage from 'pages/products/ProductJourneyPage';
import ProductRelationsPage from 'pages/products/ProductRelationsPage';
import { CreateNewSegment, MySegments } from 'pages/segments';
import { useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { PrivateRoute, SegmentAnalytics } from 'utils';
import { useCheckStoreHasSelectedPlan } from 'utils/useCheckStoreHasSelectedPlan';
import { userDetailsState } from './store/userDetailsSlice';

export default function App() {
  const { loggedIn, currentStore } = useSelector(userDetailsState);

  useCheckStoreHasSelectedPlan();

  interface LocationState {
    from: Location;
    background: Location;
  }
  const location = useLocation<LocationState>();

  // Store the previous query params when redirect to login page from a URL
  const from = location.state && location.state.from;
  const history = useHistory();
  const modalBackground = location.state && location.state.background;

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => {
        if (process.env.NODE_ENV === 'production') {
          resetError();
          history.replace('/');
        }

        return (
          <div className="grid h-screen place-items-center">
            <LoadingSpinner className="w-16 h-16 text-segments-accent-color-orange" />
          </div>
        );
      }}
    >
      <ToastContainer
        position="top-right"
        className="w-auto text-base"
        limit={1}
        theme="colored"
      />

      <SegmentAnalytics />
      <Switch location={modalBackground ?? location}>
        {/* Public Routes Start */}
        <Route exact path="/">
          {loggedIn ? (
            <Redirect
              to={{
                pathname: '/dashboard',
                search: currentStore.serializedStoreName,
              }}
            />
          ) : (
            <Redirect to="/login" />
          )}
        </Route>
        <Route exact path="/login">
          {loggedIn ? <Redirect to={from ?? '/'} /> : <LoginPage />}
        </Route>
        <Route exact path="/register">
          {loggedIn ? <Redirect to={from ?? '/'} /> : <RegisterPage />}
        </Route>
        <Route exact path="/account/reset-password" component={ResetPassword} />
        <Route exact path="/account/new-password" component={NewPassword} />
        <Route
          exact
          path="/integrations/shopify"
          component={ShopifyAuthentication}
        />
        <Route
          exact
          path="/integrations/shopify/welcome"
          component={ShopifyWelcome}
        />
        <Route exact path="/account/invitation/" component={Invitation} />
        {/* Public Routes End */}
        {/* Private Routes Start */}
        <PrivateRoute
          exact
          path="/account/change-plan"
          component={ChangePlanPage}
        />
        <PrivateRoute
          exact
          path="/account/charge_confirmation"
          component={ChargeConfirmationPage}
          noLayout
        />
        <PrivateRoute exact path="/dashboard" component={DashboardPage} />
        <PrivateRoute
          exact
          path="/analytics/summary"
          component={ExecutiveSummary}
        />
        <PrivateRoute exact path="/customers/segments" component={MySegments} />
        <PrivateRoute
          exact
          path="/customers/create"
          component={CreateNewSegment}
        />
        <PrivateRoute
          exact
          path="/customers/create/:id"
          component={CreateNewSegment}
        />
        <PrivateRoute
          exact
          path="/analytics/purchase-timing"
          component={PurchaseTiming}
        />
        <PrivateRoute path="/analytics/cohort" component={CustomerCohort} />
        <PrivateRoute exact path="/integrations" component={IntegrationsPage} />
        <PrivateRoute
          exact
          path="/integrations/connect"
          component={IntegrationsPage}
        />
        <PrivateRoute
          path="/integrations/connect/:id"
          component={IntegrationsPage}
        />
        <PrivateRoute
          path="/integrations/import/:id"
          component={IntegrationsPage}
        />
        <PrivateRoute exact path="/account" component={Settings} />
        <PrivateRoute
          exact
          path="/account/change-password"
          component={ChangePassword}
        />
        <PrivateRoute
          exact
          path="/account/change-email"
          component={ChangeEmailConfirmation}
        />
        <PrivateRoute exact path="/account/referrals" component={Referrals} />
        <PrivateRoute exact path="/account/stores" component={ManageStores} />
        <PrivateRoute
          exact
          path="/account/stores/add"
          component={AddStore}
          noLayout
        />
        <PrivateRoute
          exact
          path="/account/confirm"
          component={ConfirmPage}
          noLayout
        />

        <PrivateRoute path="/onboarding" component={OnboardingPage} noLayout />
        <PrivateRoute exact path="/dashboard/welcome" component={WelcomePage} />
        <PrivateRoute
          exact
          path="/products/product-journey"
          component={ProductJourneyPage}
        />
        <PrivateRoute
          exact
          path="/products/product-relations"
          component={ProductRelationsPage}
        />
        <PrivateRoute path="/products/cohort" component={ProductCohort} />
        <PrivateRoute path="*" component={NotFoundPage} />
      </Switch>
      {/* Component that opens in a modal with its own link should always be outside the main switch - more info consult React Router Modal example setup */}
      <PrivateRoute
        exact
        path="/dashboard/playbook"
        component={LifecyclePlaybook}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/facebook"
        component={FacebookConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/klaviyo"
        component={KlaviyoConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/import/klaviyo"
        component={KlaviyoListImport}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/drip"
        component={DripConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/activecampaign"
        component={ActiveCampaignConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/mailchimp"
        component={MailChimpConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/google"
        component={GoogleConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/omnisend"
        component={OmnisendConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/postscript"
        component={PostscriptConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/integrations/connect/yotpo"
        component={YotpoConnect}
        noLayout
      />
      <PrivateRoute
        exact
        path="/customers/segments/integrations"
        component={SegmentIntegrations}
        noLayout
      />
    </Sentry.ErrorBoundary>
  );
}
